/**
 * Modal dialog showing charts of Time Analysis.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */

import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useState } from 'react'
import LineChart from './LineChart'
import ModalDialog from './ModalDialog'

const MUI_SELECT_COLOR_HIGHLIGHT = '#aaa9a9'
const LINE_COLOR_AI = '#faa091'

/**
 * A series of time analysis data.
 */
export class TimeAnalysisSeries {
  xTimeList: number[]
  yAiProbabilityList: number[]
  name: string
  isAi: boolean
  color?: string

  constructor(
    xTimeList: number[],
    yAiProbabilityList: number[],
    name: string,
    isAi?: boolean,
    color?: string
  ) {
    if (xTimeList.length !== yAiProbabilityList.length) {
      throw new RangeError(
        'xList and yList arguments must have the same length.'
      )
    }

    this.xTimeList = xTimeList
    this.yAiProbabilityList = yAiProbabilityList
    this.name = name
    this.color = color
    this.isAi = isAi ?? false
  }
}

interface TimeAnalysisDialogProps {
  id?: string
  currentSeriesIndex?: number
  series: TimeAnalysisSeries[]
  changeVisible: (isVisible: boolean) => void
  isVisible: boolean
}

interface ItemSelectProps {
  currentSeriesIndex: number
  series: TimeAnalysisSeries[]
  handleSelectChange: (event: SelectChangeEvent<number>) => void
}

/**
 * Drop-down selection component.
 */
const ItemSelect = (props: ItemSelectProps) => {
  return (
    <FormControl
      sx={{
        m: 1,
        minWidth: 400,
        // display: 'block',
        '& label': {
          color: MUI_SELECT_COLOR_HIGHLIGHT, // default label color
        },
        '& label.Mui-focused': {
          color: MUI_SELECT_COLOR_HIGHLIGHT, // label color on focus
        },
        '&:hover label': {
          color: MUI_SELECT_COLOR_HIGHLIGHT, // Hovered label color
        },
      }}
      size="small"
    >
      <InputLabel id="demo-select-small-label">File name</InputLabel>
      <Select<number>
        labelId="chart-select-label"
        id="chart-select"
        value={props.currentSeriesIndex}
        label="File name"
        onChange={props.handleSelectChange}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: MUI_SELECT_COLOR_HIGHLIGHT, // border color
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: MUI_SELECT_COLOR_HIGHLIGHT, // focused border
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: MUI_SELECT_COLOR_HIGHLIGHT, // hover border
          },
          '& .MuiSelect-select': {
            backgroundColor: '#fff', // background of selected value
          },
        }}
      >
        {props.series.map((series: TimeAnalysisSeries, index: number) => (
          <MenuItem key={series.name} value={index}>
            {series.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

/**
 * Modal dialog which displays the charts of the given time analysis.
 */
const TimeAnalysisDialog = (props: TimeAnalysisDialogProps) => {
  const startSeriesIndex = props.currentSeriesIndex ?? 0
  const [currentSeriesIndex, setCurrentSeriesIndex] =
    useState<number>(startSeriesIndex)

  const series = props.series[currentSeriesIndex]
  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setCurrentSeriesIndex(Number(event.target.value))
  }

  return (
    <ModalDialog
      id={props.id}
      className="ai-music-detector-time-analysis"
      title="Time Analysis"
      contents={
        <>
          <div className="chart-result-container">
            <ItemSelect
              currentSeriesIndex={currentSeriesIndex}
              series={props.series}
              handleSelectChange={handleSelectChange}
            />
          </div>

          <LineChart
            xValueList={series?.xTimeList ?? []}
            xStep={1.5}
            xMin={0}
            xAxisLabel="Time"
            xUnit="s"
            yValueList={series?.yAiProbabilityList ?? []}
            yMin={0}
            yMax={100}
            yAxisLabel="AI Probability Over Time"
            yValueLabel="Confidence"
            yUnit="%"
            skipAnimation={true}
            color={LINE_COLOR_AI}
          />
        </>
      }
      changeVisible={props.changeVisible}
      isVisible={props.isVisible}
    ></ModalDialog>
  )
}

export default TimeAnalysisDialog
